import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    fetchPayoutHistory,
    approvePayout,
    rejectPayout,
    retryPayout,
    updatePayoutAmount
} from '../apiEndpoints';
import { Button, Input, Chip, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from '@heroui/react';
import { useAuth } from '../contexts/auth-context';
import { useSearchParams } from 'react-router-dom';
import { currencyCode } from '../utils/currency';
import { wrap, sectionTitle, bodyMuted, card, surface, darkPanel, btnGhost, btnPrimary, label as labelCls } from '@/components/ui/design';

const fieldWrap = 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const fieldText = 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted';
const inputCls = { inputWrapper: `h-14 ${fieldWrap}`, input: fieldText };
const labeledInputCls = { inputWrapper: `min-h-14 ${fieldWrap}`, input: fieldText, label: 'text-grey40 dark:text-nightMuted' };
const modalCls = {
    base: 'rounded-[28px] bg-white ring-1 ring-black/[0.06] dark:bg-nightSurface dark:ring-white/10',
    header: 'px-6 pt-6 text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white',
    body: 'px-6',
    footer: 'gap-2 px-6 pb-6',
    closeButton: 'right-4 top-4 rounded-full hover:bg-grey99 dark:hover:bg-white/10',
    backdrop: 'bg-black/50',
};
const statLabel = 'text-xs font-bold uppercase tracking-[0.16em] text-grey40 dark:text-nightMuted';
const statValue = 'mt-3 text-[clamp(26px,3vw,36px)] font-bold leading-none tracking-[-0.04em] text-grey10 dark:text-white';
const statusChipCls = { base: 'h-7 rounded-full px-1', content: 'text-xs font-semibold capitalize' };
const cellLabel = 'text-xs font-semibold uppercase tracking-[0.12em] text-grey50 dark:text-nightMuted';
const cellValue = 'mt-1 text-sm font-semibold text-grey10 dark:text-white';
const smallPill = 'inline-flex h-9 min-w-0 items-center justify-center rounded-full px-4 text-sm font-semibold transition-colors';
const panelTitle = 'text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white';

interface Payout {
    payoutId: string;
    organizerId: any;
    eventId: any;
    amount: number;
    fee: number;
    netAmount: number;
    currency?: string;
    status: string;
    description?: string;
    requestedAt?: string;
    approvedAt?: string;
    processedAt?: string;
    rejectionReason?: string;
    failureReason?: string;
    organizer?: {
        firstName: string;
        lastName: string;
        email: string;
    };
}

export default function AdminPayoutApproval() {
    const { user } = useAuth();
    const [searchParams] = useSearchParams()
    const admin = searchParams.get('admin') || false;
    const queryClient = useQueryClient();
    const [selectedPayout, setSelectedPayout] = useState<Payout | null>(null);
    const [rejectionReason, setRejectionReason] = useState('');
    const [editingAmount, setEditingAmount] = useState<{ payoutId: string; amount: number; currency?: string } | null>(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const { isOpen: isAmountModalOpen, onOpen: onAmountModalOpen, onClose: onAmountModalClose } = useDisclosure();

    // Check if user is admin (you may need to adjust this based on your user model)
    const isAdmin = user?.roles?.includes('admin') || admin === 'true';

    // Fetch all payout requests (admin can see all)
    const { data: payoutHistory, isLoading } = useQuery({
        queryKey: ['adminPayoutHistory'],
        queryFn: () => fetchPayoutHistory({ page: 1, limit: 50 }),
    });

    // Mutations
    const approvePayoutMutation = useMutation({
        mutationFn: approvePayout,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminPayoutHistory'] });
        }
    });

    const rejectPayoutMutation = useMutation({
        mutationFn: ({ payoutId, rejectionReason }: { payoutId: string; rejectionReason: string }) =>
            rejectPayout(payoutId, rejectionReason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminPayoutHistory'] });
            onClose();
            setRejectionReason('');
            setSelectedPayout(null);
        }
    });

    const retryPayoutMutation = useMutation({
        mutationFn: retryPayout,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminPayoutHistory'] });
            alert('Payout reset to requested status. You can now edit the amount and approve it.');
        },
        onError: (error: any) => {
            console.error('Error retrying payout:', error);
            alert(`Failed to reset payout: ${error.message || 'Unknown error'}`);
        }
    });

    const updateAmountMutation = useMutation({
        mutationFn: ({ payoutId, amount }: { payoutId: string; amount: number }) =>
            updatePayoutAmount(payoutId, amount),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['adminPayoutHistory'] });
            onAmountModalClose();
            setEditingAmount(null);
            alert('Payout amount updated successfully');
        },
        onError: (error: any) => {
            console.error('Error updating payout amount:', error);
            alert(`Failed to update payout amount: ${error.message || 'Unknown error'}`);
        }
    });

    const handleApprove = async (payoutId: string) => {
        if (confirm('Are you sure you want to approve this payout request?')) {
            try {
                await approvePayoutMutation.mutateAsync(payoutId);
            } catch (error) {
                console.error('Error approving payout:', error);
            }
        }
    };

    const handleReject = (payout: Payout) => {
        setSelectedPayout(payout);
        onOpen();
    };

    const handleRetry = async (payoutId: string) => {
        if (confirm('Are you sure you want to retry this failed payout? This will reset it to requested status so you can edit the amount before approving.')) {
            try {
                await retryPayoutMutation.mutateAsync(payoutId);
            } catch (error) {
                console.error('Error retrying payout:', error);
            }
        }
    };

    const handleEditAmount = (payout: Payout) => {
        setEditingAmount({ payoutId: payout.payoutId, amount: payout.amount, currency: payout.currency });
        onAmountModalOpen();
    };

    const handleUpdateAmount = async () => {
        if (!editingAmount || editingAmount.amount <= 0) {
            alert('Please enter a valid amount');
            return;
        }

        try {
            await updateAmountMutation.mutateAsync(editingAmount);
        } catch (error) {
            console.error('Error updating amount:', error);
        }
    };

    const confirmReject = async () => {
        if (!selectedPayout || !rejectionReason.trim()) {
            alert('Please provide a rejection reason');
            return;
        }

        try {
            await rejectPayoutMutation.mutateAsync({
                payoutId: selectedPayout.payoutId,
                rejectionReason: rejectionReason.trim()
            });
        } catch (error) {
            console.error('Error rejecting payout:', error);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'requested': return 'warning';
            case 'approved': return 'primary';
            case 'processing': return 'secondary';
            case 'completed': return 'success';
            case 'failed': return 'danger';
            case 'cancelled': return 'default';
            case 'rejected': return 'danger';
            default: return 'default';
        }
    };

    const formatCurrency = (amount: number, code?: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currencyCode(code)
        }).format(amount);
    };

    const formatTotalsByCurrency = (field: 'netAmount' | 'fee') => {
        const totals = new Map<string, number>();
        allPayouts.forEach((payout: Payout) => {
            const code = currencyCode(payout.currency);
            totals.set(code, (totals.get(code) || 0) + (payout[field] || 0));
        });
        if (totals.size === 0) return formatCurrency(0);
        return Array.from(totals).map(([code, total]) => formatCurrency(total, code)).join(' · ');
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const requestedPayouts = payoutHistory?.payouts?.filter((payout: Payout) =>
        payout.status === 'requested'
    ) || [];

    const allPayouts = payoutHistory?.payouts || [];

    // Check admin access
    if (!isAdmin) {
        return (
            <div className={`${wrap} py-6`}>
                <div className="text-center py-12">
                    <p className="text-red-600 dark:text-red-400">Access denied. Admin privileges required.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`${wrap} py-6`}>
            <div className="mb-8">
                <h1 className={sectionTitle}>Payout Approval Management</h1>
                <p className={`mt-3 ${bodyMuted}`}>Review and approve payout requests from event organizers</p>
            </div>

            <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
                <div className={`${darkPanel} rounded-[28px] p-6`}>
                    <div className="pointer-events-none absolute -right-12 -top-12 -z-10 h-40 w-40 rounded-full bg-nightAccent/25 blur-[50px] max-md:hidden" />
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/60">Pending Requests</p>
                    <p className="mt-3 text-[clamp(30px,3.6vw,40px)] font-bold leading-none tracking-[-0.04em] text-white">
                        {requestedPayouts.length}
                    </p>
                </div>
                <div className={`${surface} p-6`}>
                    <p className={statLabel}>Total Payouts</p>
                    <p className={statValue}>
                        {allPayouts.length}
                    </p>
                </div>
                <div className={`${surface} p-6`}>
                    <p className={statLabel}>Total Amount</p>
                    <p className={`${statValue} break-words text-primary dark:text-nightAccent`}>
                        {formatTotalsByCurrency('netAmount')}
                    </p>
                </div>
                <div className={`${surface} p-6`}>
                    <p className={statLabel}>Total Fees</p>
                    <p className={`${statValue} break-words`}>
                        {formatTotalsByCurrency('fee')}
                    </p>
                </div>
            </div>

            <div className={`mb-6 ${card} overflow-hidden`}>
                <div className="px-5 pt-6 pb-4 sm:px-8">
                    <h2 className={panelTitle}>Pending Approval Requests</h2>
                </div>
                <div>
                    {isLoading ? (
                        <div className="text-center py-10">
                            <p className={bodyMuted}>Loading payout requests...</p>
                        </div>
                    ) : requestedPayouts.length > 0 ? (
                        <div className="divide-y divide-black/[0.06] border-t border-black/[0.06] dark:divide-white/10 dark:border-white/10">
                            {requestedPayouts.map((payout: Payout) => (
                                <div key={payout.payoutId} className="px-5 py-5 sm:px-8">
                                    <div className="mb-4 flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <p className="break-all font-bold text-grey10 dark:text-white">Payout #{payout.payoutId}</p>
                                            <p className="mt-0.5 break-words text-sm text-grey40 dark:text-nightMuted">
                                                Organizer: {payout.organizer?.firstName} {payout.organizer?.lastName} ({payout.organizer?.email})
                                            </p>
                                            <p className="text-sm text-grey40 dark:text-nightMuted">
                                                Event: {payout.eventId?.title || 'Unknown Event'}
                                            </p>
                                        </div>
                                        <div className="flex shrink-0 flex-col items-end gap-1.5 text-right">
                                            <p className="text-lg font-bold tracking-[-0.02em] text-grey10 dark:text-white">{formatCurrency(payout.netAmount, payout.currency)}</p>
                                            <Chip
                                                color={getStatusColor(payout.status)}
                                                variant="flat"
                                                size="sm"
                                                classNames={statusChipCls}
                                            >
                                                {payout.status}
                                            </Chip>
                                        </div>
                                    </div>
                                    <div className="mb-4 grid grid-cols-2 gap-4 rounded-[20px] bg-grey99 p-4 text-sm md:grid-cols-4 dark:bg-white/[0.04]">
                                        <div>
                                            <p className={cellLabel}>Gross Amount</p>
                                            <p className={cellValue}>{formatCurrency(payout.amount, payout.currency)}</p>
                                        </div>
                                        <div>
                                            <p className={cellLabel}>Platform Fee</p>
                                            <p className={cellValue}>{formatCurrency(payout.fee, payout.currency)}</p>
                                        </div>
                                        <div>
                                            <p className={cellLabel}>Net Amount</p>
                                            <p className={cellValue}>{formatCurrency(payout.netAmount, payout.currency)}</p>
                                        </div>
                                        <div>
                                            <p className={cellLabel}>Requested</p>
                                            <p className={cellValue}>{payout.requestedAt ? formatDate(payout.requestedAt) : '-'}</p>
                                        </div>
                                    </div>
                                    {payout.description && (
                                        <p className="mb-4 text-sm text-grey40 dark:text-nightMuted">{payout.description}</p>
                                    )}
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            color="primary"
                                            size="sm"
                                            variant="light"
                                            className={`${smallPill} bg-transparent text-grey10 ring-1 ring-grey90 data-[hover=true]:bg-grey99 dark:text-white dark:ring-white/15 dark:data-[hover=true]:bg-white/[0.06]`}
                                            onClick={() => handleEditAmount(payout)}
                                        >
                                            Edit Amount
                                        </Button>
                                        <Button
                                            color="success"
                                            size="sm"
                                            className={`${smallPill} bg-primary text-white data-[hover=true]:bg-primary30 dark:bg-nightAccent dark:text-nightBg`}
                                            onClick={() => handleApprove(payout.payoutId)}
                                            isLoading={approvePayoutMutation.isPending}
                                        >
                                            Approve
                                        </Button>
                                        <Button
                                            color="danger"
                                            size="sm"
                                            variant="light"
                                            className={`${smallPill} bg-transparent text-red-600 ring-1 ring-red-500/30 data-[hover=true]:bg-red-500/10 dark:text-red-400`}
                                            onClick={() => handleReject(payout)}
                                        >
                                            Reject
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="px-5 pb-8 sm:px-8">
                            <div className="rounded-[20px] bg-grey99 px-6 py-12 text-center dark:bg-white/[0.04]">
                                <p className={bodyMuted}>No pending payout requests</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={`${card} overflow-hidden`}>
                <div className="px-5 pt-6 pb-4 sm:px-8">
                    <h2 className={panelTitle}>All Payouts History</h2>
                </div>
                <div>
                    {isLoading ? (
                        <div className="text-center py-10">
                            <p className={bodyMuted}>Loading payout history...</p>
                        </div>
                    ) : allPayouts.length > 0 ? (
                        <div className="divide-y divide-black/[0.06] border-t border-black/[0.06] dark:divide-white/10 dark:border-white/10">
                            {allPayouts.map((payout: Payout) => (
                                <div key={payout.payoutId} className="px-5 py-4 sm:px-8">
                                    <div className="flex items-center justify-between gap-4">
                                        <div className="min-w-0">
                                            <p className="break-all font-bold text-grey10 dark:text-white">#{payout.payoutId}</p>
                                            <p className="text-sm text-grey40 dark:text-nightMuted">
                                                {payout.organizer?.firstName} {payout.organizer?.lastName} - {payout.eventId?.title || 'Event'}
                                            </p>
                                        </div>
                                        <div className="flex shrink-0 flex-col items-end gap-1.5 text-right">
                                            <p className="font-bold text-grey10 dark:text-white">{formatCurrency(payout.netAmount, payout.currency)}</p>
                                            <Chip
                                                color={getStatusColor(payout.status)}
                                                variant="flat"
                                                size="sm"
                                                classNames={statusChipCls}
                                            >
                                                {payout.status}
                                            </Chip>
                                        </div>
                                    </div>
                                    {payout.rejectionReason && (
                                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                                            Rejected: {payout.rejectionReason}
                                        </p>
                                    )}
                                    {payout.failureReason && (
                                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                                            Failed: {payout.failureReason}
                                        </p>
                                    )}
                                    {payout.status === 'failed' && (
                                        <div className="mt-3">
                                            <Button
                                                color="primary"
                                                size="sm"
                                                variant="light"
                                                className={`${smallPill} bg-transparent text-grey10 ring-1 ring-grey90 data-[hover=true]:bg-grey99 dark:text-white dark:ring-white/15 dark:data-[hover=true]:bg-white/[0.06]`}
                                                onClick={() => handleRetry(payout.payoutId)}
                                                isLoading={retryPayoutMutation.isPending}
                                            >
                                                Reset to Requested
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="px-5 pb-8 sm:px-8">
                            <div className="rounded-[20px] bg-grey99 px-6 py-12 text-center dark:bg-white/[0.04]">
                                <p className={bodyMuted}>No payout history found</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <Modal isOpen={isOpen} onClose={onClose} classNames={modalCls}>
                <ModalContent>
                    <ModalHeader>Reject Payout Request</ModalHeader>
                    <ModalBody>
                        <p className="mb-2 text-sm text-grey40 dark:text-nightMuted">
                            Are you sure you want to reject this payout request? Please provide a reason for the rejection.
                        </p>
                        {selectedPayout && (
                            <div className={`mb-2 ${surface} space-y-1 p-4`}>
                                <p className="text-sm text-grey20 dark:text-nightTextSecondary">
                                    <strong className="text-grey10 dark:text-white">Payout:</strong> #{selectedPayout.payoutId}
                                </p>
                                <p className="text-sm text-grey20 dark:text-nightTextSecondary">
                                    <strong className="text-grey10 dark:text-white">Amount:</strong> {formatCurrency(selectedPayout.netAmount, selectedPayout.currency)}
                                </p>
                                <p className="text-sm text-grey20 dark:text-nightTextSecondary">
                                    <strong className="text-grey10 dark:text-white">Organizer:</strong> {selectedPayout.organizer?.firstName} {selectedPayout.organizer?.lastName}
                                </p>
                            </div>
                        )}
                        <Input
                            label="Rejection Reason"
                            placeholder="Enter reason for rejection..."
                            value={rejectionReason}
                            onChange={(e) => setRejectionReason(e.target.value)}
                            isRequired
                            classNames={labeledInputCls}
                        />
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="light" className={`${btnGhost} bg-transparent`} onPress={onClose}>
                            Cancel
                        </Button>
                        <Button
                            color="danger"
                            className="inline-flex h-12 items-center justify-center rounded-full bg-red-500 px-6 text-sm font-semibold text-white hover:bg-red-600"
                            onPress={confirmReject}
                            isLoading={rejectPayoutMutation.isPending}
                            disabled={!rejectionReason.trim()}
                        >
                            Reject Payout
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={isAmountModalOpen} onClose={onAmountModalClose} classNames={modalCls}>
                <ModalContent>
                    <ModalHeader>Edit Payout Amount</ModalHeader>
                    <ModalBody>
                        <p className="mb-2 text-sm text-grey40 dark:text-nightMuted">
                            Update the payout amount. The platform fee will be automatically recalculated based on the event's fee rate.
                        </p>
                        {editingAmount && (
                            <div className={`mb-2 ${surface} p-4`}>
                                <p className="break-all text-sm text-grey20 dark:text-nightTextSecondary"><strong className="text-grey10 dark:text-white">Payout ID:</strong> {editingAmount.payoutId}</p>
                            </div>
                        )}
                        <div className="mb-2">
                            <label className={labelCls}>
                                New Amount ({currencyCode(editingAmount?.currency)})
                            </label>
                            <Input
                                type="number"
                                value={editingAmount?.amount?.toString() || ''}
                                onChange={(e) => setEditingAmount(prev =>
                                    prev ? { ...prev, amount: parseFloat(e.target.value) || 0 } : null
                                )}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                                classNames={inputCls}
                            />
                        </div>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            color="default"
                            variant="light"
                            className={`${btnGhost} bg-transparent`}
                            onPress={onAmountModalClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            color="primary"
                            className={`${btnPrimary} no-global-button-color`}
                            onPress={handleUpdateAmount}
                            isLoading={updateAmountMutation.isPending}
                            disabled={!editingAmount || editingAmount.amount <= 0}
                        >
                            Update Amount
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </div>
    );
}
